import React, { useMemo } from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import ReactTable from '@teetsh/app/src/components/Table/ReactTable';
import {
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  CellContext,
} from '@tanstack/react-table';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { format } from '@teetsh/app/src/utils/time/format';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import {
  RestorableItemType,
  RestoreFn,
} from '@teetsh/app/src/containers/UserSettings/Trash/components/RestorableList';

interface Props<ItemsType> {
  items: ItemsType[];
  restore: RestoreFn;
  details?: (p: CellContext<ItemsType, unknown>) => React.ReactElement;
}

export function Table<ItemsType extends RestorableItemType>(
  props: Props<ItemsType>,
) {
  const { items, restore, details } = props;
  const { locale } = useUser();
  const { t } = useTranslation(['common', 'userSettings']);

  const withSchoolyearInfos = items.some(e => 'schoolyear' in e);
  const withSchoolInfos = items.some(e => 'schoolName' in e);

  const columns: ColumnDef<ItemsType>[] = useMemo(
    () => [
      {
        id: 'element',
        minSize: 200,
        header: () => t('userSettings:restore.list.element'),
        cell: p => (
          <div tw="flex flex-col gap-1">
            <div tw="font-semibold">{p.row.original.name}</div>
            {details?.(p)}
          </div>
        ),
      },
      ...(withSchoolyearInfos
        ? [
            {
              id: 'schoolSchoolyearInfos',
              minSize: 100,
              header: () =>
                t(
                  withSchoolInfos
                    ? 'userSettings:restore.list.school'
                    : 'userSettings:restore.list.schoolyear',
                ),
              cell: p => (
                <div>
                  <div tw="font-semibold">
                    {p.row.original.schoolyear - 1} - 
                    {p.row.original.schoolyear}
                  </div>
                  {'schoolName' in p.row.original && (
                    <div>{p.row.original.schoolName}</div>
                  )}
                </div>
              ),
            },
          ]
        : []),
      {
        id: 'createdAt',
        maxSize: 0,
        header: () => t('userSettings:restore.list.createdAt'),
        cell: p => (
          <div tw="whitespace-pre">
            {t('common:day_at_time', {
              day: format(
                p.row.original.createdAt,
                locale,
                t('common:datePattern.dd/MM/yyyy'),
              ),
              separator: '\n',
              time: format(
                p.row.original.createdAt,
                locale,
                t('common:time.pattern_HH_mm'),
              ),
            })}
          </div>
        ),
      },
      {
        id: 'deletedAt',
        maxSize: 0,
        header: () => t('userSettings:restore.list.deletedAt'),
        cell: p => (
          <div tw="whitespace-pre">
            {t('common:day_at_time', {
              day: format(
                p.row.original.deletedAt,
                locale,
                t('common:datePattern.dd/MM/yyyy'),
              ),
              separator: '\n',
              time: format(
                p.row.original.deletedAt,
                locale,
                t('common:time.pattern_HH_mm'),
              ),
            })}
          </div>
        ),
      },
      {
        id: 'actions',
        maxSize: 0,
        header: () => '',
        cell: p => {
          const item = p.row.original;
          return (
            <Tooltip2 label={t('userSettings:restore.button')}>
              <Button
                icon={ArrowUturnLeftIcon}
                aria-label="restaurer"
                onClick={() =>
                  restore({
                    id: item.id,
                    ...('schoolyearId' in item && {
                      schoolyearId: item.schoolyearId,
                    }),
                    ...('schoolId' in item && {
                      schoolId: item.schoolId,
                    }),
                    ...('sequenceId' in item && {
                      sequenceId: item.sequenceId,
                    }),
                    ...('type' in item && {
                      type: item.type,
                    }),
                  })
                }
              />
            </Tooltip2>
          );
        },
      },
    ],
    [restore, details, locale, t, withSchoolyearInfos, withSchoolInfos],
  );

  const tableInstance = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div tw="border rounded-md text-sm">
      <ReactTable tableInstance={tableInstance} striped />
    </div>
  );
}
